import React, { useState } from 'react';
import { formatCurrency, filterInvoicesByTimeRange, getTimeRangeLabel } from '../utils/dataUtils';
import { fetchInvoiceCSVText, describeDigitalOceanError } from '../utils/csvUtils';
import { downloadTextFile, toFileSlug, todayStamp } from '../utils/downloadUtils';

export const InvoiceTable = ({ invoices, timeRange, apiToken, accountName }) => {
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState('');

  const filteredInvoices = filterInvoicesByTimeRange(invoices, timeRange);

  const downloadInvoiceCSV = async (uuid) => {
    if (!apiToken) {
      setDownloadError('This account has no API token, so the invoice cannot be downloaded.');
      return;
    }

    setDownloadError('');
    setDownloadingId(uuid);
    try {
      const csvText = await fetchInvoiceCSVText(apiToken, uuid);
      // Include account name in the file name for easier identification
      downloadTextFile(`${toFileSlug(accountName)}_invoice_${uuid}_${todayStamp()}.csv`, csvText);
    } catch (err) {
      console.error('Error downloading invoice CSV:', err);
      setDownloadError(`Could not download invoice ${uuid}. ${describeDigitalOceanError(err)}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    const periodA = a.invoice_period || '';
    const periodB = b.invoice_period || '';
    if (periodA && periodB) return periodB.localeCompare(periodA);
    const dateA = new Date(a.date || a.created_at || 0);
    const dateB = new Date(b.date || b.created_at || 0);
    return dateB - dateA;
  });

  return (
    <div className="table-container">
      <h3 className="chart-title">Recent invoices</h3>

      {downloadError && (
        <div className="alert" role="alert">
          <span>{downloadError}</span>
          <button type="button" className="close-btn" onClick={() => setDownloadError('')} aria-label="Dismiss error">×</button>
        </div>
      )}

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">Invoice ID</th>
              <th scope="col">Period</th>
              <th scope="col" className="numeric">Amount</th>
              <th scope="col" className="numeric">Download</th>
            </tr>
          </thead>
          <tbody>
            {sortedInvoices.length === 0 ? (
              <tr>
                <td colSpan="4" className="table-empty">
                  No invoices for {getTimeRangeLabel(timeRange)}. Try a longer time range.
                </td>
              </tr>
            ) : (
              sortedInvoices.map(invoice => {
                const amount = Number.parseFloat(invoice.amount) || 0;
                const isDownloading = downloadingId === invoice.invoice_uuid;
                return (
                  <tr key={invoice.invoice_uuid}>
                    <td className="invoice-id" title={invoice.invoice_uuid}>{invoice.invoice_uuid || 'Unknown'}</td>
                    <td>{invoice.invoice_period || 'N/A'}</td>
                    <td className="numeric">{formatCurrency(amount)}</td>
                    <td className="numeric">
                      <button
                        type="button"
                        className="csv-btn"
                        id={`csv-btn-${invoice.invoice_uuid}`}
                        onClick={() => downloadInvoiceCSV(invoice.invoice_uuid)}
                        disabled={isDownloading}
                        aria-label={`Download CSV for invoice ${invoice.invoice_period || invoice.invoice_uuid}`}
                      >
                        {isDownloading ? 'Loading...' : 'CSV'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
